import React from 'react';
import type { OperatorEventState } from '../types/security';

interface OperatorStateBadgeProps {
  state: OperatorEventState;
  snoozedUntil?: string | null;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return 'expired';
  const days = Math.round(diffMs / (24 * 3600 * 1000));
  if (days >= 1) return `${days}d`;
  const hours = Math.max(1, Math.round(diffMs / (3600 * 1000)));
  return `${hours}h`;
}

const STYLES: Record<OperatorEventState, { bg: string; fg: string; label: string }> = {
  active: { bg: 'transparent', fg: 'var(--text-secondary)', label: '' },
  acknowledged: { bg: '#e0f2fe', fg: '#075985', label: 'Acknowledged' },
  snoozed: { bg: '#fef3c7', fg: '#92400e', label: 'Snoozed' },
  dismissed_fp: { bg: '#f3f4f6', fg: '#374151', label: 'False positive' },
};

/**
 * WQ-283 (DEF-023): OperatorStateBadge — renders operator-action state with
 * a distinct visual axis from severity/status (which use SeverityBadge +
 * StatusBadge respectively). For `active`, renders a subtle dot indicator
 * rather than a full chip to avoid noise on the default state.
 */
export function OperatorStateBadge({
  state,
  snoozedUntil,
}: OperatorStateBadgeProps): React.JSX.Element | null {
  if (state === 'active') {
    // WQ-287: role="img" so aria-label is valid (axe aria-prohibited-attr rule).
    // The dot is purely a visual indicator — img role + aria-label conveys
    // its meaning to screen readers without introducing interactive semantics.
    return (
      <span
        role="img"
        aria-label="Operator state: active"
        title="Active — no operator action yet"
        style={{
          display: 'inline-block',
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          background: 'var(--severity-info-text, #9ca3af)',
        }}
      />
    );
  }

  const s = STYLES[state];
  const relative = state === 'snoozed' ? formatRelative(snoozedUntil) : '';
  const fullLabel = state === 'snoozed' && relative
    ? `${s.label} (${relative})`
    : s.label;

  return (
    <span
      role="img"
      aria-label={`Operator state: ${state}`}
      style={{
        display: 'inline-block',
        fontSize: '0.75rem',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        background: s.bg,
        color: s.fg,
        fontWeight: 600,
      }}
    >
      {fullLabel}
    </span>
  );
}
